import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useDisclosure } from "@jumbo/components/JumboDialog/hooks/useDisclosure";
import DialogDefault from "@jumbo/components/JumboDialog/components/DialogDefault";
import DialogConfirm from "@jumbo/components/JumboDialog/components/DialogConfirm";

import Stack from "@mui/material/Stack";
import {
  Button,
  Chip,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Card, CardContent } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ClearIcon from "@mui/icons-material/Clear";
import ContactPageIcon from "@mui/icons-material/ContactPage";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Tooltip from "@mui/material/Tooltip";

import { usePagination } from "app/hooks/usePagination";
import { Table } from "app/components";
import AddLead from "./add-lead/index";
import ImportLeads from "./import-leads/index";
import Div from "@jumbo/shared/Div";
import {
  LEAD_STATUSES,
  LEAD_STATUS_COLORS,
  createLead,
  deleteLead,
  getAssignableUsers,
  getLeadList,
  updateLead,
} from "app/redux/slice/leadSlice";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import Loader from "@jumbo/components/Loader";
import { userType } from "app/helpers/userType";
import { buildWhatsAppUrl, openWhatsApp } from "app/helpers/whatsappLink";
import { leadDetail, PARTY_NUMBER_ADD } from "app/routes/constant";

const formatDateTime = (value) => {
  const date = new Date(value);
  const pad = (n) => String(n).padStart(2, "0");
  const hours24 = date.getHours();
  const hours12 = hours24 % 12 || 12;
  return `${pad(date.getDate())}/${pad(
    date.getMonth() + 1
  )}/${date.getFullYear()}, ${hours12}:${pad(date.getMinutes())} ${
    hours24 >= 12 ? "PM" : "AM"
  }`;
};

const startOfDay = (value) => {
  const d = new Date(value);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const isReminderDue = (reminderDate) =>
  startOfDay(reminderDate) <= startOfDay(new Date());

// Party Number expects a bare 10-digit local number, but a lead's phoneNumber
// (WhatsApp-sourced) usually carries the "91" country code prefix - strip it
// down to the last 10 digits so the prefilled Add Party Number form validates.
const toLocalPhoneNumber = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length > 10 ? digits.slice(-10) : digits;
};

const Lead = () => {
  const { isOpen, onClose, onOpen, stageAction, actionProps } = useDisclosure();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { isMA } = userType();

  const leadData = useSelector((state) => state?.lead);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [assignedToFilter, setAssignedToFilter] = useState("");
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [onPartyFilter, setOnPartyFilter] = useState("All");
  const [dueTodayLeads, setDueTodayLeads] = useState([]);

  const fetchDueTodayLeads = async () => {
    try {
      const response = await axios.get("lead/list", {
        params: { status: "Reminder Follow-up" },
      });
      const leads = response?.data?.data?.leadList ?? [];
      setDueTodayLeads(
        leads.filter((lead) => lead.reminderDate && isReminderDue(lead.reminderDate))
      );
    } catch (error) {
      setDueTodayLeads([]);
    }
  };

  useEffect(() => {
    dispatch(getAssignableUsers());
    fetchDueTodayLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    isOpen: isDelete,
    onClose: closeDelete,
    actionProps: deleteActionProps,
    stageAction: deleteStageAction,
  } = useDisclosure();
  const {
    isOpen: isImportOpen,
    onClose: closeImport,
    onOpen: openImport,
  } = useDisclosure();
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "lead" });
  const [permissionData] = useState(
    JSON.parse(localStorage.getItem("permission"))
  );
  const [currentUser] = useState(JSON.parse(localStorage.getItem("User")));

  const [permission, setPermission] = useState({
    createPermission: false,
    updatePermission: false,
    deletePermission: false,
  });
  const getPermission = () => {
    if (isMA) {
      setPermission({
        createPermission: true,
        updatePermission: true,
        deletePermission: true,
      });
    } else {
      let permissionValue = {
        createPermission: false,
        updatePermission: false,
        deletePermission: false,
      };
      const rolePermission = permissionData?.filter((item) => {
        return item?.moduleName?.includes("Lead") && item;
      });
      rolePermission?.length &&
        rolePermission.forEach(function (num) {
          if (num?.name === "create-lead") {
            permissionValue.createPermission = true;
          }
          if (num?.name === "delete-lead") {
            permissionValue.deletePermission = true;
          }
          if (num?.name === "update-lead") {
            permissionValue.updatePermission = true;
          }
        });
      setPermission(permissionValue);
    }
  };
  useEffect(() => {
    getPermission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const notifyAndRefresh = (response, onSuccess) => {
    const isSuccess = response.status === 200;
    enqueueSnackbar(response.data?.responseMessage, {
      variant: isSuccess ? "success" : "error",
    });
    if (isSuccess) onSuccess?.();
  };

  const handleAssignToMe = async (row) => {
    const response = await dispatch(
      updateLead({ id: row._id, data: { assignedTo: currentUser?._id } })
    ).unwrap();
    notifyAndRefresh(response, getData);
  };

  const columns = [
    {
      // Not sortable: the numbers are unpadded (LO-1 … LO-10), so the server's
      // string sort would order them LO-1, LO-10, LO-2. The list is already in
      // creation order by default, which is the same order as the numbers.
      id: "leadNumber",
      label: "Lead No",
      minWidth: 90,
      render: (row) => row.leadNumber || "-",
    },
    { id: "name", label: "Name", sort: true },
    { id: "phoneNumber", label: "Phone Number", sort: true },
    { id: "source", label: "Source", sort: true },
    {
      id: "status",
      label: "Status",
      sort: true,
      render: (row) => (
        <Chip
          size="small"
          label={row.status}
          color={LEAD_STATUS_COLORS[row.status] || "default"}
        />
      ),
    },
    {
      id: "onParty",
      label: "On-Party",
      align: "center",
      minWidth: 90,
      render: (row) =>
        row.onParty ? (
          <Chip size="small" label="ON" color="success" />
        ) : (
          "-"
        ),
    },
    {
      id: "assignedTo",
      label: "Assigned To",
      render: (row) => {
        if (row.assignedTo?.name) {
          return row.assignedTo.name;
        }
        if (isMA) {
          return "-";
        }
        return (
          <Tooltip title="Assign to me">
            <IconButton size="small" onClick={() => handleAssignToMe(row)}>
              <PersonAddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      id: "reminderDate",
      label: "Reminder Follow-up",
      render: (row) => {
        if (row.status !== "Reminder Follow-up" || !row.reminderDate) {
          return "-";
        }
        const isDue = isReminderDue(row.reminderDate);
        return (
          <Typography
            variant="body2"
            sx={{
              color: isDue ? "error.main" : "inherit",
              fontWeight: isDue ? "bold" : "normal",
            }}
          >
            {formatDateTime(row.reminderDate)}
            {isDue ? " (Due)" : ""}
          </Typography>
        );
      },
    },
    {
      id: "lastMessageAt",
      label: "Last Message At",
      render: (row) => (row.lastMessageAt ? formatDateTime(row.lastMessageAt) : "-"),
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 233,
      align: "right",
      render: (row) => {
        const canWhatsApp = Boolean(buildWhatsAppUrl(row.phoneNumber));
        return (
        <>
          <Tooltip
            title={canWhatsApp ? "Chat on WhatsApp" : "No valid WhatsApp number"}
          >
            {/* span keeps the tooltip working while the button is disabled */}
            <span>
              <IconButton
                disabled={!canWhatsApp}
                onClick={() => openWhatsApp(row.phoneNumber)}
              >
                <WhatsAppIcon
                  sx={{ color: canWhatsApp ? "#25D366" : "inherit" }}
                />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton onClick={() => navigate(leadDetail(row._id))}>
            <VisibilityIcon />
          </IconButton>
          {permission.updatePermission && (
            <Tooltip
              title={
                row.partyNumberId
                  ? "Already converted to Party Number"
                  : "Convert to Party Number"
              }
            >
              <span>
                <IconButton
                  disabled={Boolean(row.partyNumberId)}
                  onClick={() =>
                    navigate(PARTY_NUMBER_ADD, {
                      state: {
                        leadId: row._id,
                        name: row.name,
                        phoneNumbers: row.phoneNumber
                          ? [toLocalPhoneNumber(row.phoneNumber)]
                          : [""],
                        city: row.city,
                        belongsTo: row.assignedTo || null,
                      },
                    })
                  }
                >
                  <ContactPageIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {permission.updatePermission && (
            <IconButton
              onClick={() => {
                stageAction(row);
              }}
            >
              <EditIcon />
            </IconButton>
          )}
          {permission.deletePermission && (
            <IconButton
              onClick={() => {
                deleteStageAction(row);
              }}
            >
              <DeleteIcon color={"error"} />
            </IconButton>
          )}
        </>
        );
      },
    },
  ];

  const handleSubmit = async (data) => {
    const newLeadData = {
      name: data.name?.trim(),
      phoneNumber: data.phoneNumber?.trim(),
      email: data.email?.trim(),
      address: data.address?.trim(),
      city: data.city?.trim(),
      state: data.state?.trim(),
      country: data.country?.trim(),
      status: data.status,
      assignedTo: data.assignedTo || null,
      reminderDate:
        data.status === "Reminder Follow-up" ? data.reminderDate : null,
      onParty: Boolean(data.onParty),
    };
    const response = await dispatch(
      actionProps?._id
        ? updateLead({ id: actionProps._id, data: newLeadData })
        : createLead(newLeadData)
    ).unwrap();
    notifyAndRefresh(response, () => {
      getData();
      fetchDueTodayLeads();
      onClose();
    });
  };

  const DeleteLead = () => {
    dispatch(deleteLead(deleteActionProps?._id))
      .unwrap()
      .then((res) =>
        notifyAndRefresh(res, () => {
          getData();
          fetchDueTodayLeads();
          closeDelete();
        })
      );
  };

  const getData = () => {
    const value = {
      page: page,
      perPage: rowsPerPage,
      search: searchText.trim(),
      ...(statusFilter && { status: statusFilter }),
      ...(assignedToFilter && { assignedTo: assignedToFilter }),
      ...(onPartyFilter !== "All" && { onParty: onPartyFilter === "On" }),
    };
    if (orderBy !== "") {
      value.sort = order === "asc" ? 1 : -1;
      value.field = orderBy;
    }
    dispatch(getLeadList(value));
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    rowsPerPage,
    dispatch,
    order,
    orderBy,
    statusFilter,
    assignedToFilter,
    onPartyFilter,
  ]);

  // Refreshing only once the import dialog is dismissed keeps its summary
  // readable - reloading the list swaps this whole page for a <Loader />, which
  // would unmount the dialog mid-read.
  const handleImportClose = () => {
    closeImport();
    getData();
    fetchDueTodayLeads();
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleChangePage(0, 0);
      getData();
    }
  };

  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
  };

  return (
    <>
      {leadData.loading ? (
        <Loader />
      ) : (
        <>
          <Card sx={{ display: "flex", mb: 3 }}>
            <Div sx={{ display: "flex", flexDirection: "column", flex: "1" }}>
              <CardContent>
                <Grid container spacing={{ xs: 2, md: 3 }}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyPress={handleKeyPress}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            style={{
                              display: searchText === "" ? "none" : "flex",
                            }}
                            onClick={() => setSearchText("")}
                          >
                            <ClearIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="lead-status-filter-label">
                        Status
                      </InputLabel>
                      <Select
                        labelId="lead-status-filter-label"
                        label="Status"
                        value={statusFilter}
                        onChange={(e) => {
                          handleChangePage(0, 0);
                          setStatusFilter(e.target.value);
                        }}
                      >
                        <MenuItem value="">
                          <em>All</em>
                        </MenuItem>
                        {LEAD_STATUSES.map((status) => (
                          <MenuItem key={status} value={status}>
                            {status}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="lead-assigned-filter-label">
                        Assigned To
                      </InputLabel>
                      <Select
                        labelId="lead-assigned-filter-label"
                        label="Assigned To"
                        value={assignedToFilter}
                        onChange={(e) => {
                          handleChangePage(0, 0);
                          setAssignedToFilter(e.target.value);
                        }}
                      >
                        <MenuItem value="">
                          <em>All</em>
                        </MenuItem>
                        <MenuItem value="unassigned">
                          <em>Unassigned</em>
                        </MenuItem>
                        {leadData?.assignableUsers?.map((userOption) => (
                          <MenuItem key={userOption._id} value={userOption._id}>
                            {userOption.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="lead-on-party-filter-label">
                        On-Party
                      </InputLabel>
                      <Select
                        labelId="lead-on-party-filter-label"
                        label="On-Party"
                        value={onPartyFilter}
                        onChange={(e) => {
                          handleChangePage(0, 0);
                          setOnPartyFilter(e.target.value);
                        }}
                      >
                        <MenuItem value="All">
                          <em>All</em>
                        </MenuItem>
                        <MenuItem value="On">On</MenuItem>
                        <MenuItem value="Off">Off</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      onClick={() => {
                        handleChangePage(0, 0);
                        getData();
                      }}
                    >
                      Submit
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Div>
          </Card>
          {dueTodayLeads.length > 0 && (
            <Card sx={{ mb: 3, borderLeft: 4, borderColor: "error.main" }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <Chip label={dueTodayLeads.length} color="error" size="small" />
                  <Typography variant="h4" mb={0}>
                    Today&apos;s Reminder Follow-ups
                  </Typography>
                </Stack>
                <Stack divider={<Divider />} spacing={1.5}>
                  {dueTodayLeads.map((lead) => (
                    <Stack
                      key={lead._id}
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      spacing={1}
                    >
                      <Div>
                        <Typography variant="body1" fontWeight="bold" mb={0}>
                          {lead.name || "-"} · {lead.phoneNumber}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={0}>
                          Due: {formatDateTime(lead.reminderDate)}
                          {lead.assignedTo?.name
                            ? ` · Assigned to ${lead.assignedTo.name}`
                            : " · Unassigned"}
                        </Typography>
                      </Div>
                      <Tooltip title="View Lead">
                        <IconButton
                          size="small"
                          onClick={() => navigate(leadDetail(lead._id))}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardContent>
              <Stack
                justifyContent="space-between"
                direction={{ xs: "column", sm: "row" }}
                sx={{ mb: 3 }}
              >
                <Typography variant={"h2"} mb={0}>
                  Lead Management
                </Typography>
                {permission.createPermission && (
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{ mt: { xs: 1, md: 0 } }}
                  >
                    <LoadingButton
                      onClick={openImport}
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                    >
                      Import
                    </LoadingButton>
                    <LoadingButton
                      onClick={() => {
                        onOpen();
                      }}
                      type="submit"
                      variant="contained"
                    >
                      Add Lead
                    </LoadingButton>
                  </Stack>
                )}
              </Stack>
              <Table
                columns={columns}
                rows={leadData.list}
                page={page}
                rowsPerPage={rowsPerPage}
                Order={order}
                count={leadData.total}
                OrderBy={orderBy}
                sortData={sortData}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                editAction={stageAction}
              />
            </CardContent>
          </Card>
          <DialogDefault
            open={isOpen}
            onClose={onClose}
            content={
              <AddLead
                value={actionProps}
                onClose={onClose}
                handleSubmit={handleSubmit}
              />
            }
            title={actionProps?._id ? "Update Lead" : "Add Lead"}
            fullWidth={true}
            maxWidth={"sm"}
            actions={<></>}
          />
          <DialogDefault
            open={isImportOpen}
            onClose={handleImportClose}
            content={<ImportLeads onClose={handleImportClose} />}
            title="Import Leads"
            fullWidth={true}
            maxWidth={"sm"}
            actions={<></>}
          />
          <DialogConfirm
            isOpen={isDelete}
            onClose={closeDelete}
            title="Are you sure you want to delete this lead?"
            onConfirm={DeleteLead}
            btnLabels={{ cancel: "Cancel", confirm: "Delete" }}
          />
        </>
      )}
    </>
  );
};

export default Lead;
